import React, { MutableRefObject, useEffect, useState } from "react";
import * as cmd from './Commands';
import { AbsType, Status } from "./generated/wss_pb";

type Props = Readonly<{
    port: SerialPort;
    onDisconnect: (reason: string) => void;
    absTypeRef: MutableRefObject<AbsType>;
    isMfgMode: boolean;
}>;

export const StatusView = ({ port, onDisconnect, absTypeRef, isMfgMode }: Props): JSX.Element => {
    const [status, setStatus] = useState<Status>();

    useEffect(() => {
        let done = false;

        (async () => {
            while (!done) {
                const resp = await cmd.getStatus(port, onDisconnect);

                if (!resp) {
                    break;
                }

                setStatus(resp.getStatus());
                await new Promise(f => setTimeout(f, 100));
            }
        })();

        return () => { done = true; };
    }, [port, onDisconnect]);

    // TODO: can MK60 send fault light state too?
    const absType = absTypeRef.current;
    const hasControllerSensors = (absType !== AbsType.GENERICONOFF);
    const hasWheelSpeeds = (absType === AbsType.MK60 || absType === AbsType.MK60E1 || absType === AbsType.MK60E5);
    const hasFaultLight = (absType === AbsType.MK60E1 || absType === AbsType.MK60E5);
    const hasInputPressureSensor = (absType === AbsType.MK60E1 || absType === AbsType.MK60E5);
    const hasWheelPressureSensors = absType === AbsType.MK60E5;
    // ST_DSC frame decoding is shared by the E1 and E5
    const hasDscStatus = (absType === AbsType.MK60E1 || absType === AbsType.MK60E5);

    const selfTest = status?.getSelftest();
    const selfTestResult = selfTest ?
            ("LF: " + (selfTest.getAnaloglfok() ? "PASS" : "FAIL") + "\n" +
             "RF: " + (selfTest.getAnalogrfok() ? "PASS" : "FAIL") + "\n" +
             "LR: " + (selfTest.getAnaloglrok() ? "PASS" : "FAIL") + "\n" +
             "RR: " + (selfTest.getAnalogrrok() ? "PASS" : "FAIL"))
            : "Not run";

    const gridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'auto auto', gap: '6px 12px', alignItems: 'center', justifyItems: 'start', justifyContent: 'start' };
    const sectionStyle: React.CSSProperties = { gridColumn: '1 / -1' };
    const labelStyle: React.CSSProperties = { justifySelf: 'end' };

    return (
        <> {status && (<>
            <h2>Status</h2>

            <div style={gridStyle}>
                <h3 style={sectionStyle}>Brake switch</h3>
                <span style={labelStyle}>Brake switch input</span>
                <span>{status?.getBrakeswitchinput() ? 'pressed' : 'not pressed'}</span>
                {hasDscStatus ? (<>
                    <span style={labelStyle}>ABS brake switch input</span>
                    <span>{decode(absBrakeSwitchNames, status.getAbsbrakeswitch())}</span>
                    <span style={labelStyle}>ABS braking state</span>
                    <span>{decode(absBrakingStateNames, status.getAbsbrakingstate())}</span>
                </>) : (<>
                    <span style={labelStyle}>ABS brake switch input</span>
                    <span>{status?.getAbsbrakeswitchstate() ? 'pressed' : 'not pressed'}</span>
                </>)}
                { false && (<><span style={labelStyle}>Internal temp</span><span>{status?.getInternaltemperature()} C</span></>)}
                <h3 style={sectionStyle}>CAN</h3>
                <span style={labelStyle}>CAN receive count</span>
                <span>{status?.getCanrxcounter()}</span>
                <span style={labelStyle}>CAN transmit OK count</span>
                <span>{status?.getCantxokcounter()}</span>
                <span style={labelStyle}>CAN transmit ERR count</span>
                <span>{status?.getCantxerrcounter()}</span>
                {hasFaultLight && (<>
                    <span style={labelStyle}>ABS fault light on</span>
                    <span>{status?.getFaultlighton() ? 'yes' : 'no'}</span>
                </>)}
                {hasDscStatus && (<>
                    <span style={labelStyle}>ST_ABS</span>
                    <span>{decode(stAbsNames, status.getAbsstabs())}</span>
                    <span style={labelStyle}>ST_DSC</span>
                    <span>{decode(stDscNames, status.getAbsstdsc())}</span>
                </>)}

                {hasControllerSensors && (<h3 style={sectionStyle}>Controller sensors</h3>)}
                {hasWheelSpeeds && (<>
                    <h4 style={sectionStyle}>Wheel Speeds</h4>
                    <span style={labelStyle}>Vehicle speed</span>
                    <span>{speed(status.getVehiclespeed(), status.getVehiclespeedinvalid())}</span>
                    <CornersView corners={[
                        speed(status.getWsslf(), status.getWsslfinvalid()),
                        speed(status.getWssrf(), status.getWssrfinvalid()),
                        speed(status.getWsslr(), status.getWsslrinvalid()),
                        speed(status.getWssrr(), status.getWssrrinvalid()),
                    ]} labelStyle={labelStyle} />
                </>)}
                {(hasInputPressureSensor || hasWheelPressureSensors) && (<h4 style={sectionStyle}>Brake Pressure</h4>)}
                {hasInputPressureSensor && (<>
                    <span style={labelStyle}>Input pressure</span>
                    <span>{status?.getBrakepressinput()}</span>
                </>)}
                {hasWheelPressureSensors && (
                    <CornersView corners={[status.getBrakepresslf(), status.getBrakepressrf(), status.getBrakepresslr(), status.getBrakepressrr()]} labelStyle={labelStyle} />
                )}
                {isMfgMode && (<>
                    <span style={labelStyle}>Self test:</span>
                    <span style={{ whiteSpace: 'pre-line' }}>{selfTestResult}</span>
                </>)}
            </div>
        </>)} </>
    );
};

// ST_DSC (0x19E) signal values, from mk60e5.dbc
const absBrakeSwitchNames = ['off', 'on', 'switch fault', 'invalid (not connected?)'];
const absBrakingStateNames = ['not braking', 'braking', 'switch invalid', 'switch and pressure invalid'];
const stAbsNames = ['normal', 'fallback level', 'malfunction', 'invalid'];
const stDscNames = ['OK', 'DSC off', 'DSC fault', 'steering angle invalid', 'DTC / M mode', 'ABS fault', 'startup / sensor problem', 'invalid'];

const decode = (names: string[], value: number): string => `${names[value] ?? 'unknown'} (${value})`;

// The firmware decides whether a speed is invalid
const speed = (value: number, invalid: boolean): number | string => invalid ? 'invalid' : value;

const cornerRow: React.CSSProperties = { display: 'inline-grid', gridTemplateColumns: '60px 60px', fontVariantNumeric: 'tabular-nums', justifyItems: 'center' };
const cornerHeader: React.CSSProperties = { fontWeight: 'bold', opacity: 0.7 };

const CornersView = ({ corners, labelStyle }: Readonly<{ corners: (number | string)[], labelStyle: React.CSSProperties }>): JSX.Element => {
    return (<>
        <span style={labelStyle} />
        <span style={{ ...cornerRow, ...cornerHeader }}>
            <span>Left</span>
            <span>Right</span>
        </span>
        <span style={labelStyle}>Front</span>
        <span style={cornerRow}>
            <span>{corners[0]}</span>
            <span>{corners[1]}</span>
        </span>
        <span style={labelStyle}>Rear</span>
        <span style={cornerRow}>
            <span>{corners[2]}</span>
            <span>{corners[3]}</span>
        </span>
    </>);
};
